import { useMemo } from "react";

import type { DipRunHistoryRow } from "~/features/dataImportProjects/types";
import { useListDataImportPipelineJobDryRunHistory } from "./useDryRunJobHistory";

// Minimal structural shape of one diagnoses element (avoids importing the @package gql type).
type DiagnosisHistoryRow = {
  id: string;
  status: string;
  requestedAt: string;
  completedAt?: string | null;
  results: { severity: string }[];
};

const DIAGNOSIS_ROW_STATUSES = ["completed", "failed", "running", "pending"] as const;
type DiagnosisRowStatus = (typeof DIAGNOSIS_ROW_STATUSES)[number];

export function useDipRunHistory(projectId: string, diagnoses: DiagnosisHistoryRow[]): DipRunHistoryRow[] {
  const { history } = useListDataImportPipelineJobDryRunHistory(projectId);
  return useMemo(() => {
    const pipelineRows: DipRunHistoryRow[] = (history ?? []).map((h) => ({
      kind: "pipeline",
      action: h.action ?? (h.parameters.dryRun ? "dryrun" : "run"),
      id: h.jobRunId,
      executedAtISO: h.executedAt,
      // legacy "submitted" rows display as running until a terminal status is recorded
      status: h.status === "completed" || h.status === "failed" ? h.status : "running",
      beJobIds: h.parameters.batchEvidencesJobIds,
      bcrJobIds: h.parameters.batchChangeRequestsJobIds,
      autoApprove: !!h.parameters.autoApprove,
      batchEvidencesJobRuns: h.response.batchEvidencesJobs ?? [],
      batchChangeRequestsJobRuns: h.response.batchChangeRequestsJobs ?? [],
    }));
    const diagnosisRows: DipRunHistoryRow[] = diagnoses.map((d) => {
      const counts = { ok: 0, warning: 0, error: 0, info: 0 };
      for (const r of d.results) {
        if (r.severity in counts) counts[r.severity as keyof typeof counts]++;
      }
      const status: DiagnosisRowStatus = DIAGNOSIS_ROW_STATUSES.includes(d.status as DiagnosisRowStatus)
        ? (d.status as DiagnosisRowStatus)
        : "running";
      return {
        kind: "diagnosis",
        action: "diagnosis",
        id: d.id,
        executedAtISO: d.completedAt ?? d.requestedAt,
        status,
        counts,
      };
    });
    return [...pipelineRows, ...diagnosisRows].sort(
      (a, b) => new Date(b.executedAtISO).getTime() - new Date(a.executedAtISO).getTime(),
    );
  }, [history, diagnoses]);
}
